import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type IntegrationSummary = {
  id: number;
  name: string;
  description: string | null;
  type: string;
  created_at: string;
  updated_at: string;
};

export type IntegrationsState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  connections: IntegrationSummary[];
};

export const integrationsInitialState: IntegrationsState = {
  status: STATUS.IDLE,
  error: null,
  connections: [],
};

function isIntegrationSummary(value: unknown): value is IntegrationSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.name === "string" &&
    (row.description === null || typeof row.description === "string") &&
    typeof row.type === "string" &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

function parseIntegrationsResponse(data: unknown): IntegrationSummary[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  return data.filter(isIntegrationSummary);
}

export const fetchIntegrations = createAsyncThunk<
  IntegrationSummary[],
  void,
  { rejectValue: string }
>("integrations/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/integrations", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    const connections = parseIntegrationsResponse(data);
    if (!connections) {
      return rejectWithValue("Unexpected response shape");
    }
    return connections;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export type CreateIntegrationPayload = {
  name?: string;
  description?: string;
  type: string;
  config: Record<string, unknown>;
};

export const createIntegration = createAsyncThunk<
  IntegrationSummary,
  CreateIntegrationPayload,
  { rejectValue: string }
>("integrations/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/integrations", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isIntegrationSummary(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

const integrationsSlice = createSlice({
  name: "integrations",
  initialState: integrationsInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchIntegrations.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchIntegrations.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.connections = action.payload;
      })
      .addCase(fetchIntegrations.rejected, (state) => {
        state.status = STATUS.FAILED;
        state.connections = [];
      })
      .addCase(createIntegration.fulfilled, (state, action) => {
        state.connections = [...state.connections, action.payload];
      });
  },
});

export const integrationsReducer = integrationsSlice.reducer;
